import dayjs from 'dayjs'
import { get, omit } from 'lodash'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { activityTimerModalInitialValues$ } from 'src/store/modules/common'
import {
  setActionCounter,
  setActivityTimerModalInitialValues,
  setActivityTimerRecording,
  setCareTeams,
  setCommonAgencyCaseManagers,
  setHeaderAddActivityBillingCodeList,
  setMedicalDeviceTypes,
  setPatientAddActivityBillingCodeList,
  setPatientCaseManagers,
  setTimerAddActivityBillingCodeList
} from 'src/store/modules/common/actionCreators'
import {
  getPatientBillingStatistics,
  patientProfileSelector,
  patientUuidSelector
} from 'src/store/modules/patient'
import { getUserActivitiesCounters } from 'src/store/modules/user'

import {
  ACTION_HUB_GET_COUNTER,
  ACTIVE_TIMERS,
  ACTIVITY_LOG_AI_HELPER,
  AGENCY_CASE_MANAGEMENT_GET_LIST,
  CARE_PLAN_GET_RESPONSIBLE_LIST,
  CARE_TEAM_MANAGEMENT_GET_LIST_POST_ITEM,
  CLINICIANS,
  COMPANY_ATTACHMENT_DELETE_PATCH,
  COMPANY_ATTACHMENT_POST,
  COMPANY_CASE_MANAGERS,
  DELETE_TIMER,
  FOLLOW_UP_CALL,
  GET_BILLING_CODES,
  GET_BILLING_PROGRAMS,
  GET_MEDICAL_DEVICE_TYPES,
  GET_USERS_WITH_ACCESS_TO_PATIENTS,
  MEASUREMENT,
  PATCH_TELEMEDICINE_OUTCOME,
  PATCH_URGENT_CARE_OUTCOME,
  PATIENT,
  PATIENTS,
  PATIENT_ACTIVITY_LOG,
  PATIENT_CALCULATE_RISK,
  PATIENT_DEMOGRAPHICS_ADDRESSES,
  PATIENT_GOALS,
  PATIENT_INTERVENTION_LIST_GET,
  PATIENT_INTERVENTION_LIST_GET_BY_PATIENT,
  PATIENT_LIST,
  POST_BILLING_CHECK_USAGE,
  START_ACTIVITY_LOG,
  STOP_ACTIVITY_LOG,
  UNLOCK_CARE_PORTAL_USER
} from 'src/constants'
import { ACTION_HUB } from 'src/scenes/ActionHub/constants'
import { showInfoModal } from 'src/services/dialogs'

export const fetchBillingCodes =
  (listKey) =>
  (uuid = '', query = {}) =>
  async (dispatch) => {
    const actionsList = {
      timer: setTimerAddActivityBillingCodeList,
      header: setHeaderAddActivityBillingCodeList,
      patient: setPatientAddActivityBillingCodeList
    }

    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_BILLING_CODES,
          params: { uuid },
          query
        }
      })

      dispatch(get(actionsList, listKey)(items))

      return items
    } catch (err) {
      catchError(err)
    }
  }

export const checkBillingCodeUsage =
  ({ payload = {} }) =>
  async () => {
    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: POST_BILLING_CHECK_USAGE
        },
        payload
      })

      return response
    } catch (err) {
      catchError(err)

      return {}
    }
  }

export const unlockUser =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: UNLOCK_CARE_PORTAL_USER,
          params
        }
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const fetchCommonAgencyCaseManagers =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: AGENCY_CASE_MANAGEMENT_GET_LIST,
          query
        }
      })

      dispatch(setCommonAgencyCaseManagers(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMedicalDeviceTypes =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_MEDICAL_DEVICE_TYPES,
          query
        }
      })

      dispatch(setMedicalDeviceTypes(items))
    } catch (err) {
      catchError(err)
    }
  }

export const addUrgentCareOutcome =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATCH_URGENT_CARE_OUTCOME,
          params
        },
        payload
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updateUrgentCareOutcome = async (config) => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: PATCH_URGENT_CARE_OUTCOME,
        params
      },
      payload
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const addEditTelemedicineOutcome = async (config = {}) => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: PATCH_TELEMEDICINE_OUTCOME,
        params
      },
      payload
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

const parseCompanyAttachmentUuid = (data) => {
  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }

  if (!data || typeof data !== 'object') {
    return ''
  }

  if (typeof data.uuid === 'string' && data.uuid.trim()) {
    return data.uuid.trim()
  }

  if (typeof data?.data?.uuid === 'string' && data.data.uuid.trim()) {
    return data.data.uuid.trim()
  }

  return ''
}

export const saveAttachment = async (config = {}) => {
  const { data = {} } = config

  try {
    const response = await httpClient.upload({
      url: {
        pathname: COMPANY_ATTACHMENT_POST
      },
      data
    })

    return parseCompanyAttachmentUuid(response?.data) || ''
  } catch (err) {
    catchError(err)

    return false
  }
}

export const updateAttachment = async (config = {}) => {
  const { params = {}, payload = {} } = config

  try {
    await httpClient.request({
      method: 'patch',
      url: {
        pathname: COMPANY_ATTACHMENT_DELETE_PATCH,
        params
      },
      payload
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const deleteAttachment = async (config = {}) => {
  const { params = {} } = config

  try {
    await httpClient.request({
      method: 'delete',
      url: {
        pathname: COMPANY_ATTACHMENT_DELETE_PATCH,
        params
      }
    })

    return true
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getPatientAddresses = async (config = {}) => {
  const { params = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_DEMOGRAPHICS_ADDRESSES,
        params
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}
export const getPatientProfile = async (config = {}) => {
  try {
    const profile = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT,
        params: config?.params || {},
        query: config?.query || {}
      }
    })

    return profile
  } catch (err) {
    catchError(err)

    return {}
  }
}

export const getMedicalDeviceTypes = async (config = {}) => {
  const { query = {} } = config

  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_MEDICAL_DEVICE_TYPES,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getMeasurementList = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: MEASUREMENT,
        params,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getPatientList = async (config = {}) => {
  const { query = {} } = config

  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENTS,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getPatients = async (config = {}) => {
  const { query = {} } = config

  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_LIST,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getActivityLogPatients =
  (config = {}) =>
  async () => {
    try {
      const items = await getPatients(config)

      return items?.map((e) => ({ ...e, patientUuid: e.uuid }))
    } catch (err) {
      catchError(err)

      return []
    }
  }

export const getCaseManagers = async (config = {}) => {
  const { query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: COMPANY_CASE_MANAGERS,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getCareTeams = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: CARE_TEAM_MANAGEMENT_GET_LIST_POST_ITEM,
        params,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const fetchCareTeams =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: CARE_TEAM_MANAGEMENT_GET_LIST_POST_ITEM,
          query
        }
      })

      dispatch(setCareTeams(items))

      return items
    } catch (err) {
      catchError(err)

      return []
    }
  }

export const getBillingCodes =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: GET_BILLING_CODES,
          params,
          query
        }
      })

      return items
    } catch (err) {
      catchError(err)

      return []
    }
  }

export const getPatientGoals =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_GOALS,
          params,
          query
        }
      })

      return items
    } catch (err) {
      catchError(err)

      return []
    }
  }

export const getGoalInterventions =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: PATIENT_INTERVENTION_LIST_GET,
          params,
          query
        }
      })

      return items
    } catch (err) {
      catchError(err)

      return []
    }
  }

export const getPatientInterventions = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_INTERVENTION_LIST_GET_BY_PATIENT,
        params,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getFollowUpCall =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      const { items } = await httpClient.request({
        method: 'get',
        url: {
          pathname: FOLLOW_UP_CALL,
          params
        }
      })

      return items?.[0] || {}
    } catch (err) {
      catchError(err)

      return {}
    }
  }

export const getResponsibleList =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      const items = await httpClient.request({
        method: 'get',
        url: {
          pathname: CARE_PLAN_GET_RESPONSIBLE_LIST,
          params,
          query
        }
      })

      return items
    } catch (err) {
      catchError(err)

      return []
    }
  }

// startTime: timerStartedSecondsAgo instead of startTime (x)

export const startActivityTimer =
  (config = {}) =>
  async (dispatch, getState) => {
    const { params = {}, payload = {} } = config
    const activityTimerModalInitialValues = activityTimerModalInitialValues$(
      getState()
    )

    try {
      const log = await httpClient.request({
        method: 'post',
        url: {
          pathname: START_ACTIVITY_LOG,
          params
        },
        payload: omit(
          {
            ...payload,
            timerStartedSecondsAgo: activityTimerModalInitialValues.startTime
              ? dayjs().unix() - activityTimerModalInitialValues.startTime
              : 0
          },
          'startTime'
        )
      })

      dispatch(
        setActivityTimerModalInitialValues({
          ...log,
          startTime: dayjs().unix()
        })
      )
      dispatch(setActivityTimerRecording(true))

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const stopActivityTimer =
  (config = {}, callback) =>
  async (dispatch, getState) => {
    const patientProfile = patientProfileSelector(getState())
    try {
      const { params = {}, payload = {} } = config

      await httpClient.request({
        method: 'post',
        url: {
          pathname: STOP_ACTIVITY_LOG,
          params
        },
        payload
      })

      dispatch(setActivityTimerRecording(false))

      const patientUuid = patientProfile?.uuid || payload?.patient?.uuid

      await Promise.all([
        dispatch(getUserActivitiesCounters()),
        patientUuid && dispatch(getPatientBillingStatistics(patientUuid))
      ])

      if (callback) {
        callback()
      }

      return true
    } catch (err) {
      catchError(err)

      const message = err?.message || null

      if (
        [
          'Item not found',
          'Can not delete activity log with end date',
          'The timer is already stopped.'
        ].includes(message)
      ) {
        try {
          await dispatch(fetchActiveActivityTimer())

          return true
        } catch (err) {
          catchError(err)

          return false
        }
      }

      return false
    }
  }

export const deleteActivityTimer =
  (config = {}) =>
  async (dispatch, getState) => {
    try {
      const { params = {} } = config

      await httpClient.request({
        method: 'delete',
        url: {
          pathname: DELETE_TIMER,
          params
        }
      })

      const patientUuid = patientUuidSelector(getState()) || null

      dispatch(setActivityTimerRecording(false))
      dispatch(setActivityTimerModalInitialValues({}))

      await Promise.all([
        dispatch(getUserActivitiesCounters()),
        patientUuid && dispatch(getPatientBillingStatistics(patientUuid))
      ])

      return true
    } catch (err) {
      catchError(err)

      const message = err?.message || null

      if (
        [
          'Item not found',
          'Can not delete activity log with end date',
          'The timer is already stopped.'
        ].includes(message)
      ) {
        await dispatch(fetchActiveActivityTimer())

        return true
      }

      return false
    }
  }

export const fetchActiveActivityTimer = () => async (dispatch, getState) => {
  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: ACTIVE_TIMERS
      }
    })

    const state = getState()

    const timerId = localStorage.getItem('timerId')

    const item = state.user.profile.sessionActivityTimer
      ? timerId && items?.find((timer) => timer.timerId === timerId)
      : items?.[0]

    if (item) {
      dispatch(
        setActivityTimerModalInitialValues({
          ...item,
          startTime: dayjs().unix() - (item.durationSeconds || 0)
        })
      )
      dispatch(setActivityTimerRecording(true))
    } else {
      dispatch(setActivityTimerModalInitialValues({}))
      dispatch(setActivityTimerRecording(false))
    }
  } catch (err) {
    catchError(err)
  }
}

export const checkActiveActivityTimer = () => async (dispatch, getState) => {
  try {
    const { items } = await httpClient.request({
      method: 'get',
      url: {
        pathname: ACTIVE_TIMERS
      }
    })

    const state = getState()

    const timerId = localStorage.getItem('timerId')

    const item = state.user.profile.sessionActivityTimer
      ? timerId && items?.find((timer) => timer.timerId === timerId)
      : items?.[0]

    if (item) {
      showInfoModal({
        content: 'You have activity recording!',
        okText: 'Ok'
      })
    }
  } catch (err) {
    catchError(err)
  }
}

export const fetchPatientCaseManagers =
  (config = {}) =>
  async (dispatch) => {
    const { query = {} } = config

    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: COMPANY_CASE_MANAGERS,
          query
        }
      })

      dispatch(setPatientCaseManagers(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getClinicians = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    const { items = [] } = await httpClient.request({
      method: 'get',
      url: {
        pathname: CLINICIANS,
        params,
        query
      }
    })

    return items
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getPatientActivityLog = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT_ACTIVITY_LOG,
        params,
        query
      }
    })

    return response
  } catch (err) {
    catchError(err)

    return {
      items: [],
      meta: {}
    }
  }
}

export const getBillingPrograms = async () => {
  try {
    const codes = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_BILLING_PROGRAMS,
        query: {
          excludeCodes: 'G2214,T2022,99492,99493,99494'
        }
      }
    })

    return codes
  } catch (err) {
    catchError(err)

    return []
  }
}

export const getTaskList = async (config = {}) => {
  const { query = {} } = config

  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: ACTION_HUB,
        query
      }
    })

    return response
  } catch (err) {
    catchError(err)

    return {
      items: [],
      meta: {}
    }
  }
}

export const getUsers = async (config = {}) => {
  const { query = {} } = config

  try {
    const response = await httpClient.request({
      method: 'get',
      url: {
        pathname: GET_USERS_WITH_ACCESS_TO_PATIENTS,
        query
      }
    })

    return response
  } catch (err) {
    catchError(err)

    return {
      items: [],
      meta: {}
    }
  }
}

export const fetchActionCounter = () => async (dispatch) => {
  try {
    const count = await httpClient.request({
      method: 'get',
      url: {
        pathname: ACTION_HUB_GET_COUNTER
      }
    })

    dispatch(setActionCounter(Array.isArray(count) ? count : []))
  } catch (err) {
    catchError(err)
  }
}

export const activityLogAiHelper =
  ({ payload = {} }) =>
  async () => {
    try {
      const response = await httpClient.request({
        method: 'post',
        url: {
          pathname: ACTIVITY_LOG_AI_HELPER
        },
        payload
      })

      return response
    } catch (err) {
      catchError(err)

      return null
    }
  }

export const recalculatePatientRisk =
  ({ patientUuid }) =>
  async () => {
    try {
      await httpClient.request({
        method: 'post',
        url: {
          pathname: PATIENT_CALCULATE_RISK,
          params: { patient: patientUuid }
        }
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }
